import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  Add16Regular,
  ArrowRepeatAll16Regular,
  Checkmark12Filled,
  ChevronLeft20Regular,
  ChevronRight20Regular,
} from "@fluentui/react-icons";
import { SearchableSelect, type SearchableSelectOption } from "../../components/SearchableSelect";
import { apiErrorMessage } from "../../common/apiErrorMessage";
import { toIsoDate } from "../../tarefas/taskDates";
import {
  deleteRecurringTask,
  getTaskCalendar,
  saveRecurringTask,
  type RecurringTask,
  type RecurringTaskPayload,
  type TaskAssignee,
  type TaskCalendar,
  type TaskUrgency,
} from "../../tarefas/tarefasApi";
import { URGENCY_OPTIONS, fieldLabel, segmentButton, segmentRadius } from "./tarefasUi";

/** Quantos dias antes de cada data a tarefa aparece na lista (RecurringTaskGenerator.LeadDays). */
const LEAD_DAYS = 14;

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const FREQUENCIES: { months: number; label: string }[] = [
  { months: 1, label: "Todos os meses" },
  { months: 3, label: "Trimestral" },
  { months: 6, label: "Semestral" },
  { months: 12, label: "Anual" },
];

const MAX_TASKS_PER_DAY = 3;

// O Tailwind pré-compilado não tem sr-only: texto só para leitores de ecrã, inline.
const visuallyHidden: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const monthKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const monthTitle = (date: Date) => {
  const text = date.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const urgencyAccent = (urgency: TaskUrgency) =>
  URGENCY_OPTIONS.find((option) => option.value === urgency)?.accent ?? "transparent";

const navButton: CSSProperties = {
  height: "var(--button-height)",
  minWidth: "var(--button-height)",
  padding: "0 10px",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-button)",
  background: "var(--background)",
  color: "var(--foreground)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  cursor: "pointer",
  fontSize: "var(--text-sm)",
};

/** `seq` muda a cada abertura: o formulário nasce de novo e nunca herda o que outra macro lá deixou. */
type Editor = { macro: RecurringTask | null; day: Date | null; seq: number };

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * O calendário "macro": o que se repete todos os meses (pagamentos do dia 5, IVA, salários…), mês a
 * mês, com as outras tarefas com data por baixo como guia. Cada macro gera sozinha a tarefa na
 * lista uns dias antes da data (LEAD_DAYS); não há nada para carregar.
 */
export function CalendarioTarefas({
  assignees,
  currentUserId,
  workOptions,
  worksLoading,
  newMacroTick,
  onChanged,
}: {
  assignees: TaskAssignee[];
  currentUserId: string | null;
  workOptions: SearchableSelectOption<number>[];
  worksLoading: boolean;
  /** Muda quando se pede uma macro nova de fora (botão do cabeçalho ou Alt+N). */
  newMacroTick: number;
  /** Uma macro mudou: as tarefas geradas também (o contador do menu tem de se refazer). */
  onChanged: () => void;
}) {
  const [month, setMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [calendar, setCalendar] = useState<TaskCalendar | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [expandedDays, setExpandedDays] = useState<ReadonlySet<string>>(() => new Set());
  const openerRef = useRef<HTMLElement | null>(null);
  const loadSeq = useRef(0);
  const editorSeq = useRef(0);
  // O pedido de "nova macro" que já existia quando o calendário montou não é um pedido novo:
  // sem isto, voltar da Lista ao Calendário reabria o formulário sozinho.
  const handledMacroTick = useRef(newMacroTick);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getTaskCalendar(monthKey(month));
      if (seq === loadSeq.current) setCalendar(data);
    } catch (loadError) {
      if (seq === loadSeq.current) setError(apiErrorMessage(loadError, "Não foi possível carregar o calendário."));
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEditor = useCallback((macro: RecurringTask | null, day: Date | null) => {
    openerRef.current = document.activeElement as HTMLElement | null;
    editorSeq.current += 1;
    setEditor({ macro, day, seq: editorSeq.current });
  }, []);

  useEffect(() => {
    if (newMacroTick === handledMacroTick.current) return;
    handledMacroTick.current = newMacroTick;
    openEditor(null, null);
  }, [newMacroTick, openEditor]);

  const closeEditor = () => {
    setEditor(null);
    // Quem abriu pode já não existir (a macro foi apagada e o cartão saiu do dia).
    requestAnimationFrame(() => {
      const opener = openerRef.current;
      if (opener && opener !== document.body && opener.isConnected) {
        opener.focus();
      } else {
        document.querySelector<HTMLElement>("[data-calendar-title]")?.focus();
      }
    });
  };

  const macrosById = useMemo(
    () => new Map((calendar?.macros ?? []).map((macro) => [macro.id, macro])),
    [calendar],
  );

  const cells = useMemo(() => {
    const offset = (month.getDay() + 6) % 7;
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const total = Math.ceil((offset + daysInMonth) / 7) * 7;
    return Array.from({ length: total }, (_, index) => {
      const dayNumber = index - offset + 1;
      return dayNumber >= 1 && dayNumber <= daysInMonth
        ? new Date(month.getFullYear(), month.getMonth(), dayNumber)
        : null;
    });
  }, [month]);

  const todayIso = toIsoDate(new Date());

  const shiftMonth = (delta: number) =>
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));

  const goToday = () => {
    const today = new Date();
    setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
        <button type="button" style={navButton} onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
          <ChevronLeft20Regular aria-hidden="true" />
        </button>
        <button type="button" style={navButton} onClick={() => shiftMonth(1)} aria-label="Mês seguinte">
          <ChevronRight20Regular aria-hidden="true" />
        </button>
        <h2
          data-calendar-title
          tabIndex={-1}
          className="text-foreground"
          style={{ fontSize: "var(--text-lg, 18px)", fontWeight: 600, margin: "0 8px", minWidth: 170, outline: "none" }}
          aria-live="polite"
        >
          {monthTitle(month)}
        </h2>
        <button type="button" style={navButton} onClick={goToday}>
          Hoje
        </button>
        <span className="text-xs text-secondary-text" style={{ marginLeft: 8 }}>
          <ArrowRepeatAll16Regular aria-hidden="true" style={{ verticalAlign: "-3px" }} /> repete-se, e aparece
          na lista {LEAD_DAYS} dias antes · • outras tarefas com data
        </span>
      </div>

      {error && (
        <div className="text-sm" role="alert" style={{ color: "var(--tt-red-fg)" }}>
          {error}
        </div>
      )}

      <div
        className="bg-card border border-border"
        style={{ borderRadius: "var(--radius)", overflow: "hidden", opacity: isLoading && calendar ? 0.6 : 1 }}
        aria-busy={isLoading}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }} aria-hidden="true">
          {WEEKDAYS.map((weekday) => (
            <div
              key={weekday}
              className="text-xs text-secondary-text"
              style={{ padding: "6px 8px", fontWeight: 600, borderBottom: "1px solid var(--border)" }}
            >
              {weekday}
            </div>
          ))}
        </div>
        <ol
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
          aria-label={`Dias de ${monthTitle(month)}`}
        >
          {cells.map((day, index) => {
            const borderStyle: CSSProperties = {
              borderRight: index % 7 === 6 ? "none" : "1px solid var(--border)",
              borderBottom: index >= cells.length - 7 ? "none" : "1px solid var(--border)",
              minHeight: 116,
              minWidth: 0,
            };
            if (!day) {
              return (
                <li
                  key={`empty-${index}`}
                  aria-hidden="true"
                  style={{ ...borderStyle, backgroundColor: "rgba(100,116,139,0.04)" }}
                />
              );
            }

            const iso = toIsoDate(day);
            const occurrences = (calendar?.occurrences ?? []).filter((item) => item.date.slice(0, 10) === iso);
            const tasks = (calendar?.tasks ?? []).filter((task) => task.dueDate?.slice(0, 10) === iso);
            const isToday = iso === todayIso;
            const dayLabel = day.toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" });

            return (
              <li
                key={iso}
                aria-current={isToday ? "date" : undefined}
                style={{ ...borderStyle, padding: 6, display: "flex", flexDirection: "column", gap: 4 }}
              >
                <div className="flex items-center" style={{ justifyContent: "space-between" }}>
                  <span
                    className="text-xs"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      fontWeight: isToday ? 700 : 500,
                      backgroundColor: isToday ? "var(--primary)" : "transparent",
                      color: isToday ? "var(--primary-foreground)" : "var(--foreground)",
                    }}
                  >
                    {day.getDate()}
                  </span>
                  <button
                    type="button"
                    className="cursor-pointer"
                    onClick={() => openEditor(null, day)}
                    aria-label={`Nova tarefa que se repete a ${dayLabel}`}
                    title="Nova tarefa que se repete neste dia"
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "var(--secondary-text)",
                      borderRadius: 999,
                      width: 22,
                      height: 22,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Add16Regular aria-hidden="true" />
                  </button>
                </div>

                {occurrences.map((occurrence) => {
                  const macro = macrosById.get(occurrence.recurringTaskId);
                  if (!macro) return null;
                  const done = occurrence.taskStatus === "Done";
                  return (
                    <button
                      key={`${occurrence.recurringTaskId}-${iso}`}
                      type="button"
                      onClick={() => openEditor(macro, null)}
                      className="cursor-pointer text-left"
                      title={`${macro.title} · ${macro.frequencyLabel}${done ? " · feita" : ""}`}
                      aria-label={`${macro.title}, ${macro.frequencyLabel}${done ? ", feita este mês" : ""}. Editar`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        width: "100%",
                        padding: "2px 6px",
                        fontSize: 12,
                        lineHeight: "18px",
                        border: "none",
                        borderLeft: `3px solid ${urgencyAccent(macro.urgency) === "transparent" ? "var(--tt-blue-fg)" : urgencyAccent(macro.urgency)}`,
                        borderRadius: 4,
                        backgroundColor: done ? "var(--tt-green-bg)" : "var(--tt-blue-bg)",
                        color: done ? "var(--tt-green-fg)" : "var(--tt-blue-fg)",
                        fontWeight: 600,
                      }}
                    >
                      {done ? (
                        <Checkmark12Filled aria-hidden="true" style={{ flexShrink: 0 }} />
                      ) : (
                        <ArrowRepeatAll16Regular aria-hidden="true" style={{ flexShrink: 0 }} />
                      )}
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textDecoration: done ? "line-through" : "none",
                        }}
                      >
                        {macro.title}
                      </span>
                    </button>
                  );
                })}

                {(expandedDays.has(iso) ? tasks : tasks.slice(0, MAX_TASKS_PER_DAY)).map((task) => (
                  <div
                    key={task.id}
                    className="text-xs"
                    title={task.title}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: task.status === "Done" ? "var(--secondary-text)" : "var(--foreground)",
                      textDecoration: task.status === "Done" ? "line-through" : "none",
                      paddingLeft: 2,
                    }}
                  >
                    • {task.title}
                    {task.status === "Done" && <span style={visuallyHidden}> (feita)</span>}
                  </div>
                ))}
                {tasks.length > MAX_TASKS_PER_DAY && (
                  <button
                    type="button"
                    className="text-xs text-secondary-text cursor-pointer text-left"
                    aria-expanded={expandedDays.has(iso)}
                    onClick={() =>
                      setExpandedDays((current) => {
                        const next = new Set(current);
                        if (next.has(iso)) next.delete(iso);
                        else next.add(iso);
                        return next;
                      })
                    }
                    style={{ paddingLeft: 2, border: "none", background: "transparent" }}
                  >
                    {expandedDays.has(iso) ? "Mostrar menos" : `+${tasks.length - MAX_TASKS_PER_DAY} tarefas`}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <section aria-labelledby="tarefas-macros-titulo" className="space-y-2">
        <h2 id="tarefas-macros-titulo" className="text-sm" style={{ fontWeight: 600 }}>
          Tarefas que se repetem
          {calendar && <span className="text-secondary-text"> · {calendar.macros.length}</span>}
        </h2>
        {calendar && calendar.macros.length === 0 ? (
          <div
            className="bg-card border border-border p-4 text-sm text-secondary-text"
            style={{ borderRadius: "var(--radius)" }}
          >
            Ainda não há nenhuma. Clique no + de um dia (ou em Nova tarefa que se repete) para pôr, por exemplo, os
            pagamentos do dia 5: a tarefa passa a aparecer sozinha na lista todos os meses.
          </div>
        ) : (
          <ul
            className="bg-card border border-border"
            style={{ borderRadius: "var(--radius)", listStyle: "none", margin: 0, padding: 0 }}
          >
            {(calendar?.macros ?? []).map((macro, index) => (
              <li key={macro.id} style={{ borderTop: index === 0 ? "none" : "1px solid var(--border)" }}>
                <button
                  type="button"
                  onClick={() => openEditor(macro, null)}
                  className="w-full text-left cursor-pointer hover:bg-accent/30 flex items-center flex-wrap"
                  style={{ gap: 12, padding: "10px 14px", border: "none", background: "transparent", color: "inherit" }}
                >
                  <span
                    className="text-xs"
                    style={{
                      minWidth: 52,
                      fontWeight: 600,
                      color: "var(--tt-blue-fg)",
                    }}
                  >
                    Dia {macro.dayOfMonth}
                  </span>
                  <span className="text-sm" style={{ fontWeight: 600, flex: "1 1 220px", minWidth: 0 }}>
                    {macro.title}
                  </span>
                  <span className="text-xs text-secondary-text">{macro.frequencyLabel}</span>
                  <span className="text-xs text-secondary-text">{macro.responsibleName ?? "Por atribuir"}</span>
                  {macro.workName && <span className="text-xs text-secondary-text">{macro.workName}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editor && (
        <MacroEditor
          key={editor.seq}
          macro={editor.macro}
          day={editor.day}
          assignees={assignees}
          currentUserId={currentUserId}
          workOptions={workOptions}
          worksLoading={worksLoading}
          onClose={closeEditor}
          onSaved={() => {
            closeEditor();
            onChanged();
            void load();
          }}
        />
      )}
    </div>
  );
}

/** Criar ou editar uma macro, numa janela por cima do calendário. */
function MacroEditor({
  macro,
  day,
  assignees,
  currentUserId,
  workOptions,
  worksLoading,
  onClose,
  onSaved,
}: {
  macro: RecurringTask | null;
  day: Date | null;
  assignees: TaskAssignee[];
  currentUserId: string | null;
  workOptions: SearchableSelectOption<number>[];
  worksLoading: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState(macro?.title ?? "");
  const [description, setDescription] = useState(macro?.description ?? "");
  const [dayOfMonth, setDayOfMonth] = useState(String(macro?.dayOfMonth ?? day?.getDate() ?? ""));
  const [intervalMonths, setIntervalMonths] = useState(macro?.intervalMonths ?? 1);
  // Mês da primeira vez ("YYYY-MM"); vazio = o servidor escolhe (este mês se o dia ainda não passou).
  const [firstMonth, setFirstMonth] = useState(
    macro ? macro.firstMonth.slice(0, 7) : day ? monthKey(day) : "",
  );
  const [urgency, setUrgency] = useState<TaskUrgency>(macro?.urgency ?? "Normal");
  const [responsibleUserId, setResponsibleUserId] = useState(
    macro ? macro.responsibleUserId ?? "" : currentUserId ?? "",
  );
  const [workId, setWorkId] = useState<number | null>(macro?.workId ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deleteRef = useRef<HTMLButtonElement>(null);
  const noRef = useRef<HTMLButtonElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Trocar o botão pela confirmação (e vice-versa) desmonta o que tinha o foco: leva-se o foco
  // para o lado seguro ("Não"), senão caía fora da janela e o Tab e o Esc deixavam de funcionar.
  useEffect(() => {
    if (confirmDelete) noRef.current?.focus();
  }, [confirmDelete]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const dayNumber = Number(dayOfMonth);
  const dayValid = Number.isInteger(dayNumber) && dayNumber >= 1 && dayNumber <= 31;

  const submit = async () => {
    if (isSaving) return;
    if (title.trim() === "" || !dayValid) {
      setError(title.trim() === "" ? "Escreva o que se repete." : "O dia tem de estar entre 1 e 31.");
      return;
    }
    setIsSaving(true);
    setError(null);
    const payload: RecurringTaskPayload = {
      title: title.trim(),
      description: description.trim() || null,
      dayOfMonth: dayNumber,
      intervalMonths,
      // Nas mensais o primeiro mês decide-o o servidor pelo dia (este mês se ainda não passou):
      // mandá-lo fixava o mês em que se clicou, mesmo depois de mudar o dia.
      firstMonth: intervalMonths > 1 && firstMonth ? `${firstMonth}-01` : null,
      urgency,
      responsibleUserId: responsibleUserId || null,
      workId,
    };
    try {
      await saveRecurringTask(macro?.id ?? null, payload);
      onSaved();
    } catch (saveError) {
      setError(apiErrorMessage(saveError, "Não foi possível guardar."));
      setIsSaving(false);
    }
  };

  const remove = async () => {
    if (!macro || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      await deleteRecurringTask(macro.id);
      onSaved();
    } catch (deleteError) {
      setError(apiErrorMessage(deleteError, "Não foi possível apagar."));
      setIsSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        backgroundColor: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "8vh 16px 16px",
        overflowY: "auto",
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="macro-editor-titulo"
        className="bg-card border border-border p-5 space-y-3"
        style={{ borderRadius: "var(--radius)", width: "min(640px, 100%)", boxShadow: "0 20px 40px rgba(0,0,0,0.25)" }}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
            return;
          }
          // Janela modal: o Tab dá a volta dentro dela em vez de fugir para o calendário por trás.
          if (event.key === "Tab") {
            const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
              (element) => element.offsetParent !== null,
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        }}
      >
        <h2 id="macro-editor-titulo" className="text-foreground" style={{ fontSize: "var(--text-lg, 18px)", fontWeight: 600 }}>
          {macro ? "Tarefa que se repete" : "Nova tarefa que se repete"}
        </h2>

        <input
          ref={titleRef}
          type="text"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="O que se repete? (ex.: Pagamentos a fornecedores)"
          aria-label="Título"
          className="w-full px-3 py-2 border border-border bg-background text-foreground"
          style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-base, 16px)", fontWeight: 600 }}
        />
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Nota (opcional)"
          aria-label="Nota"
          rows={2}
          className="w-full px-3 py-2 border border-border bg-background text-foreground text-sm"
          style={{ borderRadius: "var(--radius-button)", resize: "vertical", font: "inherit" }}
        />

        <div className="flex flex-wrap" style={{ gap: 20, alignItems: "flex-end" }}>
          <label style={{ display: "block" }}>
            <div style={fieldLabel}>Dia do mês</div>
            <input
              type="number"
              min={1}
              max={31}
              value={dayOfMonth}
              onChange={(event) => setDayOfMonth(event.target.value)}
              aria-invalid={dayValid ? "false" : "true"}
              className="px-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius-button)", height: 34, width: 80 }}
            />
          </label>

          <div role="group" aria-label="Repete">
            <div style={fieldLabel} aria-hidden="true">Repete</div>
            <div className="flex">
              {FREQUENCIES.map((option, index) => {
                const selected = intervalMonths === option.months;
                return (
                  <button
                    key={option.months}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setIntervalMonths(option.months)}
                    style={{
                      ...segmentButton(selected),
                      marginLeft: index === 0 ? 0 : -1,
                      borderRadius: segmentRadius(index, FREQUENCIES.length),
                    }}
                  >
                    {selected && <Checkmark12Filled aria-hidden="true" />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {intervalMonths > 1 && (
            <label style={{ display: "block" }}>
              <div style={fieldLabel}>Primeira vez em</div>
              <input
                type="month"
                value={firstMonth}
                onChange={(event) => setFirstMonth(event.target.value)}
                className="px-2 border border-border bg-background text-foreground text-sm"
                style={{ borderRadius: "var(--radius-button)", height: 34 }}
              />
            </label>
          )}
        </div>

        <div className="flex flex-wrap" style={{ gap: 20, alignItems: "flex-end" }}>
          <div role="group" aria-label="Urgência">
            <div style={fieldLabel} aria-hidden="true">Urgência</div>
            <div className="flex">
              {URGENCY_OPTIONS.map((option, index) => {
                const selected = urgency === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setUrgency(option.value)}
                    style={{
                      ...segmentButton(selected, option.value === "Normal" ? undefined : option.style),
                      marginLeft: index === 0 ? 0 : -1,
                      borderRadius: segmentRadius(index, URGENCY_OPTIONS.length),
                    }}
                  >
                    {selected && <Checkmark12Filled aria-hidden="true" />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <label style={{ display: "block" }}>
            <div style={fieldLabel}>Responsável</div>
            <select
              value={responsibleUserId}
              onChange={(event) => setResponsibleUserId(event.target.value)}
              className="px-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius-button)", height: 34, minWidth: 180 }}
            >
              <option value="">Por atribuir</option>
              {assignees.map((assignee) => (
                <option key={assignee.userId} value={assignee.userId}>
                  {assignee.userId === currentUserId ? `${assignee.name} (eu)` : assignee.name}
                </option>
              ))}
            </select>
          </label>

          <div style={{ width: 240 }}>
            <div style={fieldLabel} aria-hidden="true">Obra (opcional)</div>
            <SearchableSelect<number>
              value={workId}
              options={workOptions}
              loading={worksLoading}
              allowClear
              panelPosition="fixed"
              placeholder="Sem obra"
              searchPlaceholder="Código ou nome da obra…"
              emptyMessage="Nenhuma obra bate com o texto."
              ariaLabel="Obra"
              onChange={(value) => setWorkId(value)}
            />
          </div>
        </div>

        <p className="text-xs text-secondary-text">
          A tarefa aparece sozinha na lista {LEAD_DAYS} dias antes de cada data, com estes dados.
          {macro && " Mudar o dia ou a frequência refaz as próximas tarefas que ainda estão por fazer."}
        </p>

        {error && (
          <div ref={errorRef} tabIndex={-1} className="text-sm" role="alert" style={{ color: "var(--tt-red-fg)", outline: "none" }}>
            {error}
          </div>
        )}

        <div className="flex items-center flex-wrap" style={{ gap: 8, justifyContent: "space-between" }}>
          <div className="flex items-center" style={{ gap: 8 }}>
            {macro &&
              (confirmDelete ? (
                <>
                  <span id="macro-confirm" className="text-sm">
                    Deixar de repetir? As próximas tarefas por fazer saem da lista.
                  </span>
                  <button
                    type="button"
                    onClick={() => void remove()}
                    aria-describedby="macro-confirm"
                    aria-disabled={isSaving}
                    className="px-3 py-2 text-sm border border-border cursor-pointer disabled:opacity-50"
                    style={{ borderRadius: "var(--radius-button)", color: "var(--tt-red-fg)", background: "var(--background)" }}
                  >
                    Sim, deixar de repetir
                  </button>
                  <button
                    ref={noRef}
                    type="button"
                    aria-describedby="macro-confirm"
                    onClick={() => {
                      setConfirmDelete(false);
                      requestAnimationFrame(() => deleteRef.current?.focus());
                    }}
                    className="px-3 py-2 text-sm border border-border bg-background text-foreground cursor-pointer"
                    style={{ borderRadius: "var(--radius-button)" }}
                  >
                    Não
                  </button>
                </>
              ) : (
                <button
                  ref={deleteRef}
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="px-3 py-2 text-sm border border-border cursor-pointer"
                  style={{ borderRadius: "var(--radius-button)", color: "var(--tt-red-fg)", background: "var(--background)" }}
                >
                  Deixar de repetir
                </button>
              ))}
          </div>
          <div className="flex" style={{ gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30 cursor-pointer"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              aria-disabled={isSaving}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground cursor-pointer disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              {isSaving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
